import { Box, Button, LoadingOverlay } from "@mantine/core";
import useExternalApiService from "helpers/ExternalApiService";
import { observer } from "mobx-react";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams  } from 'react-router-dom';
import { store } from 'stores/Store';
import styles from './map.module.scss'
import MapCanvas, { MapCanvasHandle, MapState } from "./components/MapCanvas";
import MiniMenu from "./components/MiniMenu";
import SlideUpMenus from 'Modules/SlideUpMenus';
import RoutesTab from 'Modules/PractitionerBaseballCard/components/RoutesTab';
import { resolveFindOnMapLatLon } from 'Modules/PractitionerBaseballCard/components/RoutesTab/findOnMapPin';
import RoutingModal from './components/Routing';
import Header, { ViewType } from "./components/Header";
import { DEFAULT_VALUES, END_CHECK_IN_SCALE, FilterControl, isFilterActive } from "./components/FunnelMenu/FiltersPanel";
import { ColorType } from "./components/FunnelMenu/ColorsPanel";
import ColorLabel, { valueToColor, valueToGradientColor } from "./components/ColorLabel";
import { todayPlusFormatted } from "helpers/date";
import { NavigationService, NavigationApp, resolveNavigationLocation } from 'helpers/navigation';
import ListView from "./components/ListView";
import {
  buildCheckInFilterBody,
  buildMapFilterBody,
  shouldNotifyPreviousSalesUnavailable,
  nextPreviousSalesUnavailableState,
  PREVIOUS_SALES_UNAVAILABLE_MESSAGE
} from "./components/FunnelMenu/FiltersPanel/mapFilterBody";
import FooterMenu, { FooterMenuViewType } from "./components/FooterMenu";
import Toastr from "components/Toastr";

export const DEFAULT_PIN_COLOR = "#7E7E7E";

export interface Pin {
  id: number | string;
  latLon: [number, number];
  color?: string;
}

interface Props {
  isDesktop?: boolean;
  isDesktopDetailsOpened?: boolean;
  mapView: ViewType;
  onMapViewChange: (v: ViewType) => void;
  listViewPins: any[];
  filterControl?: FilterControl;
  colorControl?: ColorType;
  footerMenuView?: FooterMenuViewType;
  searchTerm?: string;
  mapCenterLocation?: [number, number];
  isGoRouteModalOpen: boolean;
  onFooterMenuViewChange: (v: FooterMenuViewType) => void;
  onClickFunnelButton: () => void;
  onClickSearchButton: () => void;
  onSelectPractitioner: () => void;
  onSelectLead: (placeId: string) => void;
  toggleViewType: () => void;
  onListViewPinsChange: (pins: any[]) => void;
  setIsGoRouteModalOpen: (isOpen: boolean) => void;
  // states used to control pin selection
  selectedPractitioner: any;
  onSelectedPractitioner: (p: any) => void;
  onMapSearchChange?: (s: boolean) => void;
  isMapSearchActive?: boolean;
}

const apiUrl = process.env.REACT_APP_EAI_URL;

const MapComponent = observer(({
  isDesktop = false,
  isDesktopDetailsOpened = false,
  mapView,
  onMapViewChange,
  footerMenuView,
  filterControl,
  colorControl,
  searchTerm,
  listViewPins,
  mapCenterLocation,
  isGoRouteModalOpen,
  onFooterMenuViewChange,
  onClickFunnelButton,
  onClickSearchButton,
  onSelectPractitioner,
  onSelectLead,
  toggleViewType,
  onListViewPinsChange,
  setIsGoRouteModalOpen,
  selectedPractitioner,
  onSelectedPractitioner,
  onMapSearchChange,
  isMapSearchActive,
}: Props) => {
  const { postData } = useExternalApiService(apiUrl);
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [isPinsLoading, setIsPinsLoading] = useState(false);
  const [initialPinsLoading, setInitialPinsLoading] = useState(true);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [practitionersPins, setPractitionersPins] = useState<Pin[]>([]);
  const [initialMapState, setInitialMapState] = useState<MapState>({
    latLon: [0, 0],
    focalZoom: 6,
  });
  const [onViewPracititionersIds, setOnViewPractitionersIds] = useState<Set<number | string>>(new Set());
  const [onViewPractSetLength, setOnViewPractSetLength] = useState(0);
  const [, setFetchedPractitionersIds] = useState<(number | string)[]>([]);
  const loadedPractitionersRef = useRef<any[]>();
  const [isRoutingModalOpen, setIsRoutingModalOpen] = useState(false);
  const setHasRouteChangedRef = useRef<((value: boolean) => void) | null>(null);
  const [routeData, setRouteData] = useState<number[][]>();
  const [routePins, setRoutePins] = useState<Pin[]>([]);
  const [routeStartEndPins, setRouteStartEndPins] = useState<Pin[]>([]);
  const [isColorLabelOnView, setIsColorLabelOnView] = useState(true);
  const [searchResultPins, setSearchResultPins] = useState<Pin[]>([]);
  const [searchResulLocations, setSearchResultLocations] = useState<any[]>([]);
  const [isRedoSearchVisible, setIsRedoSearchVisible] = useState(false);
  const mapCanvasRef = useRef<MapCanvasHandle>(null);
  // EIS-1088 (round 2): the server tells us when it could NOT apply the
  // Previous Sales filter (keystats outage). The ref holds the PREVIOUS response's
  // flag so the toast fires on the transition into the degraded state, not on
  // every fetch and not on every re-render.
  const [isPreviousSalesUnavailable, setIsPreviousSalesUnavailable] = useState(false);
  const wasPreviousSalesUnavailableRef = useRef(false);
  // EIS-1097: there is exactly ONE sales row now, so exactly ONE skip flag and
  // ONE toast. EIS-1093's sibling `rolling_12m_sales_unavailable` state/ref/toast
  // are deleted -- the server no longer sets that key on this endpoint, so a
  // second toast would be dead code that silently never fires.

  const isFunnelActive = !!(colorControl || isFilterActive(filterControl));
  const isSlideUpMenusOpen = store.isSlideUpMenusOpenInMap;

  const [minScaleColorLabel, maxScaleColorLabel] = useMemo(() => {
    let minScale = 0;
    let maxScale = DEFAULT_VALUES.maxDaysSinceLastCheckIn;

    if (!colorControl) {
      return [minScale, maxScale ?? END_CHECK_IN_SCALE];
    }

    if (colorControl === "last-check-in" && filterControl?.isCheckInFilterActive) {
      minScale = filterControl.minDaysSinceLastCheckIn ?? 0;
      maxScale = filterControl.maxDaysSinceLastCheckIn ?? DEFAULT_VALUES.maxDaysSinceLastCheckIn;
    } else if (colorControl === "until-follow-up") {
      const minFollowUp = filterControl?.minDaysUntilFollowUp;
      const maxFollowUp = filterControl?.maxDaysUntilFollowUp;
      minScale = (minFollowUp !== null && minFollowUp !== undefined) ? minFollowUp : 0;
      maxScale = (maxFollowUp !== null && maxFollowUp !== undefined) ? maxFollowUp : DEFAULT_VALUES.maxDaysSinceLastCheckIn;
    }

    return [minScale, maxScale ?? END_CHECK_IN_SCALE];
  }, [colorControl, filterControl]);

  const setIsSlideUpMenusOpen = (isOpen: boolean) => {
    store.setIsSlideUpMenusOpenInMap(isOpen);
  };

  const processPractitionersOverlapping = (data: { i: number, l: [number, number] }[]): Pin[] => {
    const newProcessedFeatures: Pin[] = [];

    if (!data || data.length === 0) return [];
    const offsetDistance = 0.0001;

    const groups: Record<string, number[]> = {};
    data.forEach((feature, featureIndex: number) => {
      const [x, y] = feature.l;
      const key = `${x.toFixed(4)},${y.toFixed(4)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(featureIndex);
    });
    Object.values(groups).forEach((indices) => {
      if (indices.length <= 1) {
        newProcessedFeatures.push({
          id: data[indices[0]].i,
          latLon: data[indices[0]].l,
        })
        return;
      }
      const numPins = indices.length;
      const cols = Math.ceil(Math.sqrt(numPins));
      const rows = Math.ceil(numPins / cols);
      indices.forEach((featureIndex, groupIndex) => {
        const col = groupIndex % cols;
        const row = Math.floor(groupIndex / cols);
        const baseCoords = data[featureIndex].l as [number, number];
        const offsetX = (col - (cols - 1) / 2) * offsetDistance;
        const offsetY = (row - (rows - 1) / 2) * offsetDistance;
        const offsetCoords: [number, number] = [
          baseCoords[0] + offsetX,
          baseCoords[1] + offsetY,
        ];
        newProcessedFeatures.push({
          id: data[featureIndex].i,
          latLon: offsetCoords,
        });
      });
    });

    return newProcessedFeatures;
  }

  const fetchPins = async (f?: FilterControl): Promise<Pin[] | undefined> => {
    try {
      // EIS-1088: extracted to buildMapFilterBody so the wire contract (including
      // the new `data_source` / `has_previous_sales` keys) is unit-testable.
      const filterBody: { [k: string]: any } = {
        ...buildMapFilterBody(f),
        ...buildCheckInFilterBody(f, todayPlusFormatted),
      };

      const data = await postData('/api/get_practitioners_for_chat_map', {
        "fmc_email": store.impersonatedUser,
        onlyGeoValidated: true,
        limit: 999999999,
        alias: store.impersonatedUserAlias,
        ...(filterBody),
      });

      // EIS-1088 (round 2): never let a skipped Previous Sales filter pass
      // silently -- the pins below are a SUPERSET of what was asked for.
      if (shouldNotifyPreviousSalesUnavailable(data, wasPreviousSalesUnavailableRef.current)) {
        setIsPreviousSalesUnavailable(true);
      }
      wasPreviousSalesUnavailableRef.current = nextPreviousSalesUnavailableState(
        data, wasPreviousSalesUnavailableRef.current
      );

      const processedPractitionersPins = processPractitionersOverlapping(data.p);
      setPractitionersPins(processedPractitionersPins);

      updatePinsColor(loadedPractitionersRef.current || [], colorControl, processedPractitionersPins);

      return processedPractitionersPins;
    } catch (error) {
      console.error('Error fetching practitioners:', error);
    }
  };

  const updatePinsColor = (practitioners: any[], c?: ColorType, fetchedPins?: Pin[]) => {
    const pins = fetchedPins ? [...fetchedPins] : [...practitionersPins];
    if (c === "last-check-in" || c === "until-follow-up") {
      // Get the filter range for gradient scaling
      let minScale = 0;
      let maxScale = DEFAULT_VALUES.maxDaysSinceLastCheckIn;

      if (c === "last-check-in" && filterControl?.isCheckInFilterActive) {
        const minCheckIn = filterControl.minDaysSinceLastCheckIn;
        const maxCheckIn = filterControl.maxDaysSinceLastCheckIn;
        minScale = (minCheckIn !== null && minCheckIn !== undefined) ? minCheckIn : 0;
        maxScale = (maxCheckIn !== null && maxCheckIn !== undefined) ? maxCheckIn : DEFAULT_VALUES.maxDaysSinceLastCheckIn;
      } else if (c === "until-follow-up") {
        // Handle null values explicitly by converting to number
        const minFollowUp = filterControl?.minDaysUntilFollowUp;
        const maxFollowUp = filterControl?.maxDaysUntilFollowUp;
        minScale = (minFollowUp !== null && minFollowUp !== undefined) ? minFollowUp : 0;
        maxScale = (maxFollowUp !== null && maxFollowUp !== undefined) ? maxFollowUp : DEFAULT_VALUES.maxDaysSinceLastCheckIn;
      }

      practitioners?.forEach((pract) => {
        const pinIndex = pins.findIndex((p) => p.id === pract.id);
        if (pinIndex >= 0) {
          const daysSinceLastCheckIn = (
            new Date().getTime() - new Date(pract?.check_in_created_at).getTime()
          ) / (24 * 60 * 60 * 1000);
          const daysUntilFollowUp = (
            new Date().getTime() - new Date(pract?.check_in_follow_up_date).getTime()
          ) / (24 * 60 * 60 * 1000);
          pins[pinIndex] = {
            ...(pins[pinIndex]),
            color: colorControl === "last-check-in" ?
              valueToGradientColor(daysSinceLastCheckIn, minScale as number, maxScale as number)
              : valueToGradientColor(daysUntilFollowUp, minScale as number, maxScale as number)
          }
        }
      });
      setPractitionersPins(pins);
    } else if (c === "specialty-type" || c === "status") {
      practitioners?.forEach((pract) => {
        const pinIndex = pins.findIndex((p) => p.id === pract.id);
        if (pinIndex >= 0) {
          pins[pinIndex] = {
            ...(pins[pinIndex]),
            color: colorControl === "specialty-type" ?
              valueToColor(pract?.specialty, c)
              : valueToColor(`${pract?.stage} - ${pract?.status}`, c)
          }
        }
      });
      setPractitionersPins(pins);
    } else {
      setPractitionersPins(
        pins.map((p) => ({
          ...p,
          color: DEFAULT_PIN_COLOR,
        }))
      );
    }
  }

  const loadPractitionersByIds = (ids: (number | string)[]) => {
    if (ids.length === 0) return;

    setIsPinsLoading(true);
    postData('/api/get_practitioners_by_ids', {
      "fmc_email": store.impersonatedUser,
      practitioner_ids: ids,
    }).then((r) => {
      const preSelectedPractitionerId = parseInt(urlSearchParams.get("id") || "");
      if (r?.practitioners && r?.practitioners.length > 0) {
        store.setPractitionersFilteredByFMC([
          ...store.practitionersFilteredByFMC,
          ...(r?.practitioners || []),
        ]);
        loadedPractitionersRef.current = [
          ...(loadedPractitionersRef.current || []),
          ...(r.practitioners),
        ];
        if (preSelectedPractitionerId && !selectedPractitioner) {
          const p = loadedPractitionersRef.current.find(
            (lp) => lp.id === preSelectedPractitionerId
          );
          //set practitioner pin from route params and clear params
          if (p) {
            onSelectedPractitioner(p)
            setUrlSearchParams((p) => {
              p.delete("id");
              p.delete("lat");
              p.delete("lon");
              return "";
            })
          }
        }
      }
    }).finally(() => setIsPinsLoading(false));
  };

  const handleAddToRoute = (practitioner: any) => {
    store.addToRoute(practitioner);
    if (setHasRouteChangedRef.current) {
      setHasRouteChangedRef.current(true);
    }
    onSelectedPractitioner(undefined);
  };

  const handleGoRoute = (opt: string) => {
    if (!selectedPractitioner) return;

    // EIS-979 Part 1: the minicard nav serves three shapes — practitioners
    // (default-address FK), transient Places-search location pins (latLon),
    // and route start/end locations. resolveNavigationLocation normalizes all
    // three, then the shared NavigationService navigates coords-FIRST (the old
    // address-first search returned a blank Google page for malformed
    // addresses, and location pins could crash on a missing latLon).
    const location = resolveNavigationLocation(selectedPractitioner);
    if (!location) {
      console.log('No navigation data available for selection');
      return;
    }
    NavigationService.navigateToLocation(location, opt as NavigationApp, {
      onError: (error) => console.error(`${opt} navigation failed:`, error),
    });
    setIsGoRouteModalOpen(false);
  };

  const fetchSearchResults = () => {
    const center = mapCanvasRef.current?.getViewBoxCenter();
    const viewHeight = mapCanvasRef.current?.getViewBoxRadius();
    if (!viewHeight || !center) {
      console.error("Internal error");
      return;
    }
    setIsPinsLoading(true);
    onSelectedPractitioner(null);
    postData('/api/google_places_text_search_by_bounds', {
      query: searchTerm,
      location: center,
      radius: (viewHeight / 2),
      language: "en",
    }).then((response: any) => {
      const pins: Pin[] = response?.results?.map((r: any) => ({
        id: r?.place_id,
        latLon: [r?.geometry?.location?.lat, r?.geometry?.location?.lng],
      } as Pin)) || [];
      const locations = response?.results?.map((r: any) => ({
        id: r?.place_id,
        name: r?.name,
        primary_name: r?.name,
        latLon: [r?.geometry?.location?.lat, r?.geometry?.location?.lng],
        formatted_address: r?.formatted_address,
      }));
      setSearchResultLocations(locations);
      setSearchResultPins(pins);
    }).finally(() => {
      setIsPinsLoading(false)
      setIsRedoSearchVisible(false);
    });
  }

  useEffect(() => {
    updatePinsColor(loadedPractitionersRef.current || [], colorControl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedPractitionersRef.current, colorControl]);

  useEffect(() => {
    if (onViewPracititionersIds && onViewPractSetLength > 0) {
      setFetchedPractitionersIds((prev) => {
        const notLoadedPractitionersIds = [...onViewPracititionersIds]
          .filter((onViewId) => !prev.includes(onViewId));
        loadPractitionersByIds(notLoadedPractitionersIds);
        return [
          ...prev,
          ...notLoadedPractitionersIds,
        ]
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onViewPractSetLength]);

  useEffect(() => {
    if (store?.currentDirections) {
      if (store?.currentDirections.length > 0) {
        setRouteData(store?.currentDirections);
      } else {
        setRouteData(undefined);
      }
    } else {
      setRouteData(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentDirections]);

  useEffect(() => {
    const pins: Pin[] = [];

    store.selectedPractitioners
      .filter((s) => !s.isFirstLocation && !s.isLastLocation)
      .forEach((p) => {
        const lon = parseFloat(String(p.longitude));
        const lat = parseFloat(String(p.latitude));
        pins.push({
          id: p.id,
          latLon: [lat, lon]
        })
      });

    const r: Pin[] = [];
    store.selectedPractitioners.forEach((item, index) => {
      if (item.isFirstLocation) {
        r.push({
          id: "start",
          latLon: [item.latitude, item.longitude],
        });
      }
      if (item.isLastLocation) {
        r.push({
          id: "end",
          latLon: [item.latitude, item.longitude],
        });
      }
    });
    setRouteStartEndPins(r);
    setRoutePins(pins);

    // Ensure all route practitioners are loaded, even if filtered out
    const routePractitionerIds = store.selectedPractitioners
      .filter(p => p.id && !p.isFirstLocation && !p.isLastLocation)
      .map(p => p.id);

    const unloadedRouteIds = routePractitionerIds.filter(
      id => !loadedPractitionersRef.current?.find(p => p.id === id)
    );

    if (unloadedRouteIds.length > 0) {
      loadPractitionersByIds(unloadedRouteIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.selectedPractitioners]);

  useEffect(() => {
    // EIS-1077: on direct navigation to /mapView (refresh / deep-link) this
    // component mounts before ProtectedRoute's initializeUser() has resolved
    // the view-as user, so store.impersonatedUser is still null. Skip the
    // fetch in that case (a {fmc_email: null} request always returns 0 pins)
    // and let the store.impersonatedUser dependency below re-run this effect
    // exactly once when impersonation resolves. On entry via "/",
    // AccountTypeRouter only navigates here after the user is loaded, so
    // impersonatedUser is already set at mount and never changes afterwards:
    // fetch count on that path is unchanged (EIS-1069 not worsened).
    if (!store.impersonatedUser) return;
    fetchPins(filterControl).then((practitionersPins) => {

      const lat = urlSearchParams?.get("lat");
      const lon = urlSearchParams?.get("lon");
      if (practitionersPins && practitionersPins.length > 0 &&
        (!store.initialLatLon || !(lat && lon) || (!initialMapState.latLon))
      ) {
        const firstValidPin = practitionersPins[0];
        setInitialMapState({
          ...initialMapState,
          latLon: firstValidPin.latLon,
        });
      }
    }).finally(() => setInitialPinsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterControl, store.impersonatedUser]);

  useEffect(() => {
    if (urlSearchParams.get("id")) {
      const lat = urlSearchParams.get("lat");
      const lon = urlSearchParams.get("lon");
      if (lat && lon) {
        setInitialMapState({
          ...initialMapState,
          latLon: [parseFloat(lat), parseFloat(lon)],
          focalZoom: 15,
        })
      } else {
        if (store.initialLatLon) {
          setInitialMapState({
            ...initialMapState,
            latLon: store.initialLatLon,
            focalZoom: 6,
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchParams]);

  useEffect(() => {
    if (searchTerm) {
      fetchSearchResults();
    } else {
      setSearchResultLocations([]);
      setSearchResultPins([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // tricky way to force END NAVIGATION to open routes map routes view,
  // without changing the navitionMode code
  useEffect(() => {
    if ((!selectedPractitioner && isSlideUpMenusOpen)
      && (footerMenuView !== "ROUTES")) {
      onFooterMenuViewChange("ROUTES");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPractitioner, isSlideUpMenusOpen, footerMenuView])

  useEffect(() => {
    if (mapCenterLocation) {
      mapCanvasRef.current?.centerMap(mapCenterLocation);
    }
  }, [mapCenterLocation]);

  useEffect(() => {
    if (mapView === "list") {
      const _onViewBoxPins = mapCanvasRef.current?.getViewBoxPins();
      if (_onViewBoxPins) {
        const listViewPins = _onViewBoxPins.filter(
          (pin) => loadedPractitionersRef.current?.find(
            (pract) => pract.id === pin.id
          )
        ).map((pin) => {
          const pract = loadedPractitionersRef.current?.find(
            (pract) => pract.id === pin.id
          );
          return pract;
        });

        onListViewPinsChange(listViewPins);
      }
      setIsColorLabelOnView(false);
      onSelectedPractitioner(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapView]);

  return (
    <>
      {/* EIS-1088 (round 2) / EIS-1097: the SINGLE, visible, non-silent report
          that the server could not apply the Previous Sales filter -- which now
          covers the window and the dollar range too. Uses the existing
          components/Toastr convention (same error styling as the MapRouter
          toasts). AC9: a missing window field on a partially-deployed
          microservice means UNAVAILABLE, never "zero sales", so this toast is
          the only thing standing between the user and a confidently wrong
          "nobody has bought anything". */}
      <Toastr
        closeTime={5000}
        backgroundColor={'#E64B37'}
        message={PREVIOUS_SALES_UNAVAILABLE_MESSAGE}
        isVisible={isPreviousSalesUnavailable}
        onClose={() => setIsPreviousSalesUnavailable(false)}
      />
      <Header
        isDesktop={isDesktop}
        isFunnelActive={isFunnelActive}
        isSearchModeActive={!!searchTerm}
        currentView={mapView}
        isFilterActive={isFilterActive(filterControl)}
        onFunnelClicked={onClickFunnelButton}
        onSearchModeClicked={onClickSearchButton}
        toggleViewType={toggleViewType}
        onHeaderClicked={() => {
          if (onMapSearchChange) {
            onMapSearchChange(false);
          }
        }}
      />
      <Box pos="fixed" bottom={0} left={0} style={{ zIndex: 1000 }}>
        <MiniMenu
          practitioner={!isSlideUpMenusOpen && !isDesktopDetailsOpened && selectedPractitioner}
          onSlideMenuOpen={() => {
            if (typeof selectedPractitioner.id === "string") {
              onSelectLead(selectedPractitioner.id);
            } else {
              onSelectPractitioner();
            }
          }}
          onRouteMenuOpen={() => {
            setIsGoRouteModalOpen(true);
          }}
          onAddToRoute={(p) => {
            handleAddToRoute(p);
            onFooterMenuViewChange("ROUTES");
          }}
        />
        {colorControl && (
          <div className={styles.colorLabelMobile}>
            <ColorLabel
              isVisible={isColorLabelOnView}
              colorControl={colorControl}
              minScale={minScaleColorLabel}
              maxScale={maxScaleColorLabel}
            />
          </div>
        )}
        <FooterMenu
          isDesktop={isDesktop}
          view={footerMenuView}
          isColorLabelVisible={mapView === "map"}
          colorControl={colorControl}
          minScale={minScaleColorLabel}
          maxScale={maxScaleColorLabel}
          onViewChange={onFooterMenuViewChange}
        />
      </Box>
      <div className={styles.container}>
        <LoadingOverlay
          visible={!isMapLoaded || isPinsLoading}
          zIndex={1000} />
        <SlideUpMenus
          isOpen={isSlideUpMenusOpen && selectedPractitioner}
          selectedView='baseballcard'
          setIsOpen={setIsSlideUpMenusOpen}
          onRouteChanged={(setHasRouteChanged) => {
            setHasRouteChangedRef.current = setHasRouteChanged;
          }} />
        <RoutesTab
          isOpen={isGoRouteModalOpen}
          setIsOpen={setIsGoRouteModalOpen}
          onSelect={handleGoRoute}
          pin={selectedPractitioner ?? null}
          onFindOnMap={() => {
            // EIS-1061: derive coordinates from the locally-selected pin, not
            // store.currentPractitioner (stale/unset for transient Google
            // Places search-result pins). Same resolver as RoutesTab's row
            // gate, so the rendered option always matches what we can do.
            const latLon = resolveFindOnMapLatLon(selectedPractitioner);
            if (latLon) {
              mapCanvasRef.current?.centerMap(latLon);
            }
          }} />
        <RoutingModal
          selectedPractitioners={store.selectedPractitioners}
          isOpen={isRoutingModalOpen}
          onClear={() => {
            store.setSelectedPractitioners([]);
            setIsRoutingModalOpen(false);
          }}
          handleClose={() => setIsRoutingModalOpen(false)}
        />

        {mapView === "list" && !isDesktop && (
          <Box w="100%" bg="white" pos="absolute" top="36px"
            style={{
              zIndex: 19,
              boxSizing: "border-box",
              overflowY: "scroll",
              height: "calc(100% - 106px)",
            }}
          >
            <ListView
              onPractitionerSelected={(p) => {
                onSelectedPractitioner(p);
                const defaultAddressId = p?.default_address;
                const address = p?.addresses?.find(
                  (x: any) => x.id === defaultAddressId
                );
                if (address) {
                  const latLon: [number, number] = [
                    address.latitude,
                    address.longitude
                  ];
                  mapCanvasRef.current?.centerMap(latLon);
                }
                onMapViewChange("map");
              }}
              practitioners={listViewPins}
            />
          </Box>
        )}

        {!initialPinsLoading && (
          <MapCanvas
            ref={mapCanvasRef}
            pins={practitionersPins}
            searchResultPins={searchResultPins}
            mapState={initialMapState}
            routeData={routeData}
            routePins={routePins}
            routeStartEndPins={routeStartEndPins}
            isColorLabelActive={!!colorControl}
            isColorLabelOnView={isColorLabelOnView}
            onColorLabeOnViewChange={(v) => setIsColorLabelOnView(v)}
            onMapSearchChange={(mapSearchState) => {
              if (onMapSearchChange) {
                onMapSearchChange(mapSearchState);
              }
            }}
            isMapSearchActive={isMapSearchActive}
            isModalOpen={isSlideUpMenusOpen}
            onMapLoaded={() => {
              setIsMapLoaded(true);
            }}
            selectedPinId={
              selectedPractitioner?.isFirstLocation 
                ? "start" 
                : selectedPractitioner?.isLastLocation 
                  ? "end" 
                  : selectedPractitioner?.id
            }
            selectedPractitioner={selectedPractitioner}
            onPinSelected={(id) => {
              // Handle first/last location clicks (start/end IDs)
              if (id === "start" || id === "end") {
                const location = store.selectedPractitioners.find(
                  (sp) => (id === "start" && sp.isFirstLocation) || (id === "end" && sp.isLastLocation)
                );
                if (location) {
                  onSelectedPractitioner(location);
                  setIsColorLabelOnView(false);
                  return;
                }
              }

              // First try to find in loaded practitioners
              let p = loadedPractitionersRef.current?.find((p) => p.id === id);

              // If not found in loaded practitioners, check if it's in the route
              if (!p && id) {
                p = store.selectedPractitioners.find((sp) => sp.id === id);
              }

              if (p) {
                onSelectedPractitioner(p);
                setIsColorLabelOnView(false);
              } else {
                // clicked on location
                if (typeof id === "string") {
                  const l = searchResulLocations.find(
                    (x) => x.id === id
                  );
                  if (l) {
                    onSelectedPractitioner(l);
                  } else {
                    onSelectedPractitioner(null);
                  }
                } else {
                  onSelectedPractitioner(null);
                }
              }
            }}
            onViewPins={(ids) => {
              setOnViewPractitionersIds((prev) => {
                const s = new Set([
                  ...prev,
                  ...ids
                ]);
                setOnViewPractSetLength(s.size);
                return s;
              });
            }}
            onPinsLassoed={(ids) => {
              if (!ids) {
                store.setSelectedPractitioners([]);
                setRouteData(undefined);
                setRoutePins([]);
              } else {
                loadedPractitionersRef.current?.filter(
                  (p) => ids.includes(p.id)
                ).forEach((p) => {
                  handleAddToRoute(p);
                });
              }
              setIsColorLabelOnView(false);
              onFooterMenuViewChange("ROUTES");
            }}
            onNavigateHome={() => {
              navigate(`/chatbot`)
            }}
            onViewBoxChange={() => {
              if (searchTerm) {
                setIsRedoSearchVisible(true);
              }
            }}
            isDesktop={isDesktop}
          />
        )}
        {searchTerm && isRedoSearchVisible ? (
          <Button size="compact-xs" pos="absolute" top={50} bg="#4F7D9C"
            style={{
              zIndex: 999,
              left: "50%",
              transform: "translateX(-50%)"
            }}
            onClick={() => {
              fetchSearchResults();
            }}>
            Redo Search
          </Button>
        ) : null}
      </div>
    </>
  );
}
)

export default MapComponent;